/**
 * The life of a ticket on the Kitchen Display, worked out from the timing
 * settings — when it turns amber, red, starts flashing and leaves the screen.
 *
 * Reads the tier ratios from the KDS timer itself, so this preview and the
 * cards on the line always agree.
 */
import { KDS_TIER_RATIOS, kdsStaleSeconds } from '@/components/kitchen/useKdsUrgencyTimer'

export type KitchenModeChoice = 'kds' | 'kot_only'

export interface KitchenFormState {
  mode: KitchenModeChoice
  urgencyMinutes: string
  staleMinutes: string
  recallWindowSeconds: string
  confirmKotPrint: boolean
  /** Per-order-type prep targets (minutes). Empty = use the global target. */
  prepTargetDineIn: string
  prepTargetTakeout: string
  prepTargetDelivery: string
}

/** Same bounds the save applies, so the preview shows what will be stored. */
export const TIMING_LIMITS = {
  target: { min: 1, max: 240, fallback: 15 },
  stale: { min: 15, max: 1440, fallback: 120 },
  recall: { min: 0, max: 3600, fallback: 300 },
} as const

type Limit = { min: number; max: number; fallback: number }

export function clampSetting(raw: string, limit: Limit): number {
  return Math.max(limit.min, Math.min(limit.max, Number(raw) || limit.fallback))
}

/** True when the typed value is blank, not a number, or outside the allowed range. */
export function isOutOfRange(raw: string, limit: Limit): boolean {
  const t = raw.trim()
  if (t === '') return true
  const n = Number(t)
  return !Number.isFinite(n) || n < limit.min || n > limit.max
}

export interface TicketLife {
  amberAt: number
  redAt: number
  flashingAt: number
  hiddenAt: number
}

export function ticketLife(targetMinutes: number, staleMinutes: number): TicketLife {
  const targetSeconds = Math.max(1, targetMinutes * 60)
  return {
    amberAt: targetSeconds * KDS_TIER_RATIOS.warming,
    redAt: targetSeconds * KDS_TIER_RATIOS.urgent,
    flashingAt: targetSeconds * KDS_TIER_RATIOS.critical,
    hiddenAt: kdsStaleSeconds(targetMinutes, staleMinutes),
  }
}

/** 450 → "7:30", 7200 → "2 h", 5400 → "1 h 30 min". */
export function formatElapsed(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds))
  if (s < 3600) {
    const m = Math.floor(s / 60)
    return `${m}:${String(s % 60).padStart(2, '0')}`
  }
  const h = Math.floor(s / 3600)
  const m = Math.round((s % 3600) / 60)
  return m === 0 ? `${h} h` : `${h} h ${m} min`
}

/** 300 → "5 min", 90 → "1 min 30 s", 45 → "45 s". */
export function formatSeconds(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds))
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  const rest = s % 60
  return rest === 0 ? `${m} min` : `${m} min ${rest} s`
}

export interface TicketLifeRow {
  key: string
  label: string
  life: TicketLife
}

const ORDER_TYPE_OVERRIDES: { key: string; label: string; field: keyof KitchenFormState }[] = [
  { key: 'dine_in', label: 'Dine-in', field: 'prepTargetDineIn' },
  { key: 'takeout', label: 'Takeaway', field: 'prepTargetTakeout' },
  { key: 'delivery', label: 'Delivery', field: 'prepTargetDelivery' },
]

/**
 * One row for the shared target, then one per order type that has its own.
 * An override that is blank or not a number is skipped — that type inherits.
 */
export function ticketLifeRows(form: KitchenFormState): TicketLifeRow[] {
  const target = clampSetting(form.urgencyMinutes, TIMING_LIMITS.target)
  const stale = clampSetting(form.staleMinutes, TIMING_LIMITS.stale)
  const overrides: TicketLifeRow[] = []
  for (const o of ORDER_TYPE_OVERRIDES) {
    const raw = String(form[o.field]).trim()
    if (raw === '') continue
    const n = Number(raw)
    if (!Number.isFinite(n)) continue
    const minutes = Math.max(TIMING_LIMITS.target.min, Math.min(TIMING_LIMITS.target.max, Math.round(n)))
    overrides.push({ key: o.key, label: o.label, life: ticketLife(minutes, stale) })
  }
  return [
    {
      key: 'default',
      label: overrides.length > 0 ? 'Other orders' : 'Every order',
      life: ticketLife(target, stale),
    },
    ...overrides,
  ]
}
